import {QueryOptions} from '@apollo/client';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';
import DocumentStore from 'notebook/documents/DocumentStore';

import {
  RequestV2DefinitionFileStateQuery,
  RequestV2DefinitionFileStateQueryVariables,
} from './graphql/__generated/RequestV2DefinitionFileStateQuery';
import {REQUEST_V2_DEFINITION_FILE_STATE_QUERY} from './graphql/RequestV2DefinitionQueries';
import RequestV2DefinitionTabState from './RequestV2DefinitionTabState';

type RequestV2Definition = RequestV2DefinitionFileStateQuery['requestV2Definition'];

interface RequestV2DefinitionFileStateAttributes {
  requestV2Definition?: RequestV2Definition;
  editUrl?: string;
  name?: string;
  documentId?: string;
}

class RequestV2DefinitionFileState extends ApolloFileState<
  RequestV2DefinitionFileStateQuery,
  RequestV2DefinitionFileStateQueryVariables,
  RequestV2DefinitionFileStateAttributes
> {
  static fileType = ModelTypeConstants.REQUEST_V2_DEFINITION;
  static override getQueryOptions(fileId: string): QueryOptions<RequestV2DefinitionFileStateQueryVariables> {
    return {
      query: REQUEST_V2_DEFINITION_FILE_STATE_QUERY,
      variables: {
        fileId,
      },
    };
  }

  static override dataToAttributes(
    data: RequestV2DefinitionFileStateQuery
  ): RequestV2DefinitionFileStateAttributes {
    const {requestV2Definition} = data;
    return isInaccessible(requestV2Definition)
      ? {}
      : {
          requestV2Definition,
          editUrl: requestV2Definition.editUrl,
          name: requestV2Definition.name,
          documentId: requestV2Definition.templateDocument.id,
        };
  }

  static override openFile(fileId: string): JQueryPromise<RequestV2DefinitionFileState> {
    return super.openFile(fileId) as JQueryPromise<RequestV2DefinitionFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
      hideTabNav: true,
    };
  }
  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers()
      .at(0)
      .tabs()
      .add(
        new RequestV2DefinitionTabState({
          requestV2DefinitionId: this.id,
        })
      );

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  override editURL(): string | undefined {
    return this.get('editUrl');
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }

  override hasUnsavedChanges(): boolean {
    const documentId = this.get('documentId');
    return documentId ? DocumentStore.hasUnsavedChanges(documentId) : false;
  }
}

export default RequestV2DefinitionFileState;
